import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PillRail, Pill } from "@/components/common/Segmented";
import { btn } from "@/components/common/btn";
import { createWorkOrder, getNextWONumber, getSettings, getSites } from "@/lib/api";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { emptyLineItem } from "./workOrderTabUtils";
import { money } from "@/lib/format";

const FALLBACK_LABOR_RATE = 165;
// Settings → Default Rates → "Total Labor Rate" = ceil(cost / (1 − GM)).
const laborRateFromSettings = (s) => {
  const c = parseFloat(s?.default_labor_cost) || 0;
  const gm = parseFloat(s?.default_labor_margin) || 0;
  if (!c) return FALLBACK_LABOR_RATE;
  return gm >= 100 ? c : Math.ceil(c / (1 - gm / 100));
};

const PRESETS = (rate) => ([
  { key: "labor", label: "Labor hrs", make: () => emptyLineItem("1", "Labor", 1, rate) },
  { key: "part", label: "Part", make: () => emptyLineItem("2", "", 1, 0) },
  { key: "trip", label: "Trip charge", make: () => emptyLineItem("7", "Trip charge", 1, 0) },
  { key: "sub", label: "Subcontract", make: () => emptyLineItem("3", "", 1, 0) },
]);
const COST_LABEL = { "1": "Labor", "2": "Material", "3": "Sub", "4": "Freight", "5": "Equip", "7": "Travel" };

// Quick WO — two levels: fresh (no parent → pick customer + site) or from an existing WO (copies the base info + line items, linked back).
export function QuickWOModal({ open, onOpenChange, parent, clientId: clientIdProp, clientSites: sitesProp, technicians = [], projectManagers = [], clients = [], onCreated }) {
  const [pickedClientId, setPickedClientId] = useState("");
  const [loadedSites, setLoadedSites] = useState([]);
  const clientId = clientIdProp || pickedClientId;
  const clientSites = sitesProp || loadedSites;
  useEffect(() => {
    if (sitesProp || !clientId) { setLoadedSites([]); return; }
    getSites({ client_id: clientId }).then((r) => setLoadedSites(r.data || [])).catch(() => setLoadedSites([]));
  }, [clientId, sitesProp]);
  const primarySiteId = (clientSites.find((s) => s.is_primary) || clientSites[0])?.id || "";
  const defaultPM = parent?.project_manager_id || (projectManagers.find((pm) => pm.is_default) || projectManagers[0])?.id || "";
  const [laborRate, setLaborRate] = useState(FALLBACK_LABOR_RATE);
  const [title, setTitle] = useState("");
  const [siteId, setSiteId] = useState(parent?.site_id || primarySiteId);
  const [pmId, setPmId] = useState(defaultPM);
  const [projectType, setProjectType] = useState("service");
  const [techId, setTechId] = useState(parent?.assigned_tech_id || "");
  const [date, setDate] = useState("");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  // Fresh mode defaults the site once the picked customer's sites arrive.
  useEffect(() => { if (!parent && !siteId && primarySiteId) setSiteId(primarySiteId); }, [parent, siteId, primarySiteId]);
  // PMs may load after the modal opens — default the PM without re-running the reset below.
  useEffect(() => { if (open && !pmId && defaultPM) setPmId(defaultPM); }, [open, pmId, defaultPM]);

  // Reset only when the modal opens / the parent changes (NOT when sites/PMs load — that wiped the picked customer).
  useEffect(() => {
    if (!open) return;
    setPickedClientId(""); setTitle(parent?.title || ""); setSiteId(parent?.site_id || primarySiteId); setPmId(defaultPM);
    setProjectType(parent?.project_type || "service"); setTechId(parent?.assigned_tech_id || ""); setDate("");
    const copied = (parent?.line_items || []).filter((li) => !li.is_change_order).map((li) => ({ ...emptyLineItem(li.cost_type || "2", li.description || "", li.quantity ?? 1, li.unit_price ?? 0), unit_cost: li.unit_cost, margin_pct: li.margin_pct, total: (li.quantity || 0) * (li.unit_price || 0) }));
    getSettings().then((r) => {
      const rate = laborRateFromSettings(r.data);
      setLaborRate(rate);
      setLines(copied.length ? copied : [emptyLineItem("1", "Labor", 1, rate)]);
    }).catch(() => setLines(copied.length ? copied : [emptyLineItem("1", "Labor", 1, FALLBACK_LABOR_RATE)]));
  }, [open, parent]); // eslint-disable-line react-hooks/exhaustive-deps

  const presets = useMemo(() => PRESETS(laborRate), [laborRate]);
  const update = (idx, field, value) => setLines((prev) => prev.map((li, i) => {
    if (i !== idx) return li;
    const next = { ...li, [field]: value };
    next.total = (parseFloat(next.quantity) || 0) * (parseFloat(next.unit_price) || 0);
    return next;
  }));
  const subtotal = lines.reduce((s, li) => s + (li.total || 0), 0);
  const materialSubtotal = lines.filter((li) => li.cost_type === "2").reduce((s, li) => s + (li.total || 0), 0);
  const taxRate = parent?.hide_tax ? 0 : (parseFloat(parent?.tax_rate) || 0);
  const taxAmount = materialSubtotal * (taxRate / 100);
  const total = subtotal + taxAmount;

  const create = async () => {
    if (!clientId) { toast.error("Pick a customer"); return; }
    if (!title.trim()) { toast.error("Give the work order a title"); return; }
    if (!siteId) { toast.error("Pick a site"); return; }
    if (!pmId) { toast.error("Pick a project manager"); return; }
    const items = lines.filter((li) => (li.description || "").trim() || li.total);
    setSaving(true);
    try {
      let woNum = "";
      try { woNum = (await getNextWONumber(clientId)).data.wo_number; } catch { /* backend assigns */ }
      const res = await createWorkOrder({
        client_id: clientId, site_id: siteId, title: title.trim(), wo_number: woNum || undefined,
        project_type: projectType, status: "draft",
        line_items: items, subtotal, tax_rate: parent?.tax_rate || 0, tax_amount: taxAmount, total,
        hide_tax: !!parent?.hide_tax, customer_po_number: parent?.customer_po_number || "",
        account_manager: parent?.account_manager || "", account_manager_email: parent?.account_manager_email || "",
        project_manager_id: pmId, project_manager_name: projectManagers.find((pm) => pm.id === pmId)?.name || parent?.project_manager_name || "",
        assigned_tech_id: techId || null, assigned_tech_ids: techId ? [techId] : [], lead_tech_id: techId || null,
        scheduled_date: date || null, created_from_wo_id: parent?.id || null,
        notes: [], inspection_items: [], equipment_ids: [], tm_charges: [], change_orders: [],
      });
      toast.success(`Created ${res.data.wo_number} from ${parent?.wo_number || "this WO"}`);
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the work order");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="quick-wo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Zap className="w-4 h-4 text-emerald-600" />Quick Work Order</DialogTitle>
          <DialogDescription className="text-xs">{parent ? `Copies the base info and lines from ${parent.wo_number || "this WO"}; the original is linked, never changed.` : "Fresh work order — customer, site, a title and a few lines. Opens in the editor when created."}</DialogDescription>
        </DialogHeader>
        {!clientIdProp && (
          <SearchableSelect value={pickedClientId} onValueChange={(v) => { setPickedClientId(v || ""); setSiteId(""); }} placeholder="Customer…" searchPlaceholder="Search customers…" showNone={false} className="h-9 text-sm"
            options={clients.filter((c) => !c.is_archived && c.customer_type !== "parts_counter").map((c) => ({ value: c.id, label: c.name }))} data-testid="quick-wo-customer" />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title — e.g. Replace limit switch, 5 hrs T&M" className="h-9" autoFocus data-testid="quick-wo-title" />
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="h-9 text-sm" data-testid="quick-wo-site"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>{clientSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_primary ? " ★" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PillRail testId="quick-wo-type">
            {[["service", "Service"], ["tm", "T&M"], ["project", "Project"]].map(([k, l]) => <Pill key={k} on={projectType === k} onClick={() => setProjectType(k)} testId={`quick-wo-type-${k}`}>{l}</Pill>)}
          </PillRail>
          <Select value={techId || "none"} onValueChange={(v) => setTechId(v === "none" ? "" : v)}>
            <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="quick-wo-tech"><SelectValue placeholder="Technician" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No tech yet</SelectItem>
              {technicians.filter((t) => !t.is_archived).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={pmId || "none"} onValueChange={(v) => setPmId(v === "none" ? "" : v)}>
            <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="quick-wo-pm"><SelectValue placeholder="Project manager" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Project manager…</SelectItem>
              {projectManagers.map((pm) => <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-[150px] text-xs" title="Scheduled date (optional)" data-testid="quick-wo-date" />
        </div>

        <div className="rounded-lg border">
          <div className="flex items-center gap-1.5 flex-wrap px-3 py-2 border-b bg-slate-50/60">
            <span className="text-[11px] font-semibold text-slate-500 mr-1">Add</span>
            {presets.map((p) => (
              <button key={p.key} type="button" className={btn("nav")} onClick={() => setLines((prev) => [p.make(), ...prev])} data-testid={`quick-wo-add-${p.key}`}>
                <Plus className="w-3 h-3" /> {p.label}
              </button>
            ))}
            <span className="ml-auto text-[11px] text-slate-500">Labor {money(laborRate)}/hr</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-slate-500">
                <th className="px-3 py-1.5 text-left font-semibold w-20">Type</th>
                <th className="px-2 py-1.5 text-left font-semibold">Description</th>
                <th className="px-2 py-1.5 text-right font-semibold w-20">Qty</th>
                <th className="px-2 py-1.5 text-right font-semibold w-28">Price</th>
                <th className="px-2 py-1.5 text-right font-semibold w-28">Total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((li, idx) => (
                <tr key={li.id} className="border-t" data-testid={`quick-wo-line-${idx}`}>
                  <td className="px-3 py-1 text-xs text-slate-500">{COST_LABEL[li.cost_type] || li.cost_type}</td>
                  <td className="px-2 py-1"><Input value={li.description} onChange={(e) => update(idx, "description", e.target.value)} placeholder={li.cost_type === "2" ? "Part / material" : "Description"} className="h-7 text-sm" data-testid={`quick-wo-desc-${idx}`} /></td>
                  <td className="px-2 py-1"><Input type="number" step="0.25" value={li.quantity} onChange={(e) => update(idx, "quantity", parseFloat(e.target.value) || 0)} className="h-7 text-sm text-right num" data-testid={`quick-wo-qty-${idx}`} /></td>
                  <td className="px-2 py-1"><Input type="number" step="0.01" value={li.unit_price} onChange={(e) => update(idx, "unit_price", parseFloat(e.target.value) || 0)} className="h-7 text-sm text-right num" data-testid={`quick-wo-price-${idx}`} /></td>
                  <td className="px-2 py-1 text-right num font-medium">{money(li.total || 0)}</td>
                  <td className="px-1 py-1"><button type="button" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-500" title="Remove" data-testid={`quick-wo-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
              {lines.length === 0 && <tr className="border-t"><td colSpan={6} className="px-3 py-4 text-center text-xs text-slate-400">No lines yet — use the Add buttons above.</td></tr>}
            </tbody>
          </table>
          <div className="flex items-center gap-5 px-3 py-2 border-t text-xs text-slate-600 flex-wrap">
            <span>Subtotal <b className="num text-slate-900">{money(subtotal)}</b></span>
            {taxRate > 0 && <span>Tax {taxRate}% <b className="num text-slate-900">{money(taxAmount)}</b></span>}
            <span className="ml-auto text-base font-semibold text-slate-900 num" data-testid="quick-wo-total">{money(total)}</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button type="button" className={btn("nav")} onClick={() => onOpenChange(false)} data-testid="quick-wo-cancel">Cancel</button>
          <button type="button" className={btn("write")} onClick={create} disabled={saving} data-testid="quick-wo-create">
            <Zap className="w-3.5 h-3.5" /> {saving ? "Creating…" : "Create & open"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
